import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useMemo,
  type ReactElement,
  type ReactNode,
} from "react";

export type SlotArea = {
  name: string;
  label?: string;
  accept?: string[];
  multiple?: boolean;
  fallback?:
    | ReactNode
    | ((context?: Record<string, unknown>) => ReactNode | null | undefined);
};

type SlotRender = (
  slotName: string,
  payload?: Record<string, unknown>,
) => ReactNode;

type ResolveSlotOptions = {
  renderSlot?: SlotRender;
  areaMap?: Record<string, ReactNode[]>;
  areas?: SlotArea[];
  context?: Record<string, unknown>;
};

function extractRuntimeContext(
  context?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (!context) return undefined;
  const direct = context.runtimeContext;
  if (direct && typeof direct === "object" && !Array.isArray(direct)) {
    return direct as Record<string, unknown>;
  }
  return undefined;
}

function injectRuntimeContextToNodes(
  nodes: ReactNode[],
  runtimeContext?: Record<string, unknown>,
): ReactNode[] {
  if (!runtimeContext) return nodes;
  return nodes.map((node) => {
    if (!isValidElement(node)) return node;
    const props =
      node.props && typeof node.props === "object"
        ? (node.props as Record<string, unknown>)
        : {};
    const runtime =
      props.runtime && typeof props.runtime === "object"
        ? (props.runtime as Record<string, unknown>)
        : {};
    return cloneElement(node as ReactElement<any>, {
      runtimeContext,
      __runtimeContext: runtimeContext,
      runtime: {
        ...runtime,
        context: runtimeContext,
      },
    });
  });
}

type UseSlotRuntimeOptions = {
  renderSlot?: SlotRender;
  children?: ReactNode;
  areas?: SlotArea[];
  context?: Record<string, unknown>;
};

function appendAreaNode(
  areaMap: Record<string, ReactNode[]>,
  areaName: string,
  node: ReactNode,
) {
  if (!areaMap[areaName]) {
    areaMap[areaName] = [];
  }
  areaMap[areaName].push(node);
}

function normalizeSlotNodes(node: ReactNode): ReactNode[] {
  return Children.toArray(node);
}

export function collectSlotChildren(
  children?: ReactNode,
): Record<string, ReactNode[]> {
  const areaMap: Record<string, ReactNode[]> = {};
  const visited = new WeakSet<object>();

  const collectAreaNames = (node: unknown, names: Set<string>) => {
    if (node === null || node === undefined || typeof node === "boolean")
      return;

    if (Array.isArray(node)) {
      node.forEach((item) => collectAreaNames(item, names));
      return;
    }

    if (isValidElement(node)) {
      const props =
        node.props && typeof node.props === "object"
          ? (node.props as Record<string, unknown>)
          : {};
      const areaName = String(props.__slot ?? "").trim();
      if (areaName) names.add(areaName);
      collectAreaNames(props.node, names);
      collectAreaNames(props.children, names);
      return;
    }

    if (node && typeof node === "object") {
      const objectNode = node as Record<string, unknown>;
      if (visited.has(objectNode)) return;
      visited.add(objectNode);

      const props =
        objectNode.props && typeof objectNode.props === "object"
          ? (objectNode.props as Record<string, unknown>)
          : undefined;
      const areaName = String(props?.__slot ?? "").trim();
      if (areaName) names.add(areaName);
      collectAreaNames(props?.node, names);
      collectAreaNames(props?.children, names);
      collectAreaNames(objectNode.children, names);
      collectAreaNames(objectNode.node, names);
    }
  };

  Children.toArray(children).forEach((child) => {
    if (!isValidElement(child)) return;
    const names = new Set<string>();
    collectAreaNames(child, names);
    names.forEach((areaName) => appendAreaNode(areaMap, areaName, child));
  });

  return areaMap;
}

export function resolveSlot(
  areaName: string,
  { renderSlot, areaMap = {}, areas = [], context }: ResolveSlotOptions,
): ReactNode[] | undefined {
  if (renderSlot) {
    const injectedNode = renderSlot(areaName, context);
    if (injectedNode !== undefined && injectedNode !== null) {
      return normalizeSlotNodes(injectedNode);
    }
  }

  const schemaNodes = areaMap[areaName];
  if (schemaNodes && schemaNodes.length > 0) {
    return injectRuntimeContextToNodes(
      schemaNodes,
      extractRuntimeContext(context),
    );
  }

  const fallback = areas.find((area) => area.name === areaName)?.fallback;
  if (fallback !== undefined) {
    const fallbackNode =
      typeof fallback === "function" ? fallback(context) : fallback;
    if (fallbackNode !== undefined && fallbackNode !== null) {
      return normalizeSlotNodes(fallbackNode);
    }
  }

  return undefined;
}

export function renderSlotArea(
  areaName: string,
  options: ResolveSlotOptions,
): ReactNode | null {
  const nodes = resolveSlot(areaName, options);
  if (!nodes || nodes.length === 0) return null;
  if (nodes.length === 1) return nodes[0];
  return nodes;
}

export function useSlotRuntime({
  renderSlot,
  children,
  areas = [],
  context,
}: UseSlotRuntimeOptions) {
  const areaMap = useMemo(() => collectSlotChildren(children), [children]);

  const getAreaNodes = useCallback(
    (areaName: string, payload?: Record<string, unknown>) => {
      const mergedContext =
        payload || context
          ? { ...(context || {}), ...(payload || {}) }
          : undefined;
      return resolveSlot(areaName, {
        renderSlot,
        areaMap,
        areas,
        context: mergedContext,
      });
    },
    [renderSlot, areaMap, areas, context],
  );

  const hasArea = useCallback(
    (areaName: string, payload?: Record<string, unknown>) => {
      const nodes = getAreaNodes(areaName, payload);
      return nodes !== undefined && nodes.length > 0;
    },
    [getAreaNodes],
  );

  const renderArea = useCallback(
    (areaName: string, payload?: Record<string, unknown>) => {
      const mergedContext =
        payload || context
          ? { ...(context || {}), ...(payload || {}) }
          : undefined;
      return renderSlotArea(areaName, {
        renderSlot,
        areaMap,
        areas,
        context: mergedContext,
      });
    },
    [renderSlot, areaMap, areas, context],
  );

  return {
    getAreaNodes,
    hasArea,
    renderSlotArea: renderArea,
    areaMap,
  };
}
